/**
 * WHO IS ON THIS JOB — at the top, in the colors the board already uses.
 *
 * Anthony: "All job cards need the tech and service advisor assigned easy
 * to see near the top with our current color coded bubble style."
 *
 * The technician was three tabs down inside The Work, and the advisor was
 * not recorded at all. So the two questions asked most often about a job —
 * who is doing it, who is looking after the customer — were the two the
 * card would not answer without hunting, and a customer ringing back got
 * whoever picked up rather than the person who already knows their car.
 *
 * ─── THE SAME COLORS AS THE SCHEDULE ──────────────────────────────────
 *
 * A technician's hue comes from their id, so it is the same color here,
 * on the calendar and on a list tile. That is the whole value of a color
 * code: it has to survive the journey between screens or it is decoration.
 *
 * ─── EMPTY IS A STATE, AND IT SAYS SO ──────────────────────────────────
 *
 * "Nobody on it" is drawn as a real bubble rather than left blank. A gap
 * reads as "not loaded yet"; a gray bubble reads as a job nobody has been
 * put on, which is a thing somebody needs to fix.
 */

import { db } from "../data/client.ts";
import { esc } from "./dom.ts";
import { crewHue } from "../pages/schedule.ts";

export type Person = { id: string; name: string };

const initials = (n: string) =>
  n.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "?";

/** One bubble. The hue is the person's own, so it matches the board. */
function bubble(p: Person | null, role: string): string {
  if (!p) {
    return `<span class="whob none" title="Nobody assigned">
      <i class="whob-dot">?</i><span class="whob-n">No ${esc(role)}</span></span>`;
  }
  return `<span class="whob" style="--crew:${crewHue(p.id)}" title="${esc(p.name)}">
    <i class="whob-dot">${esc(initials(p.name))}</i>
    <span class="whob-n">${esc(p.name)}</span></span>`;
}

/**
 * The bar itself. Read-only markup; `wireWhoBar` makes it editable for
 * anybody allowed to change it, so a technician looking at their own job
 * sees who is on it without being offered a control they cannot use.
 */
export function whoBarHtml(
  techs: Person[], advisor: Person | null, mayEdit: boolean,
  desk: Person[] = [],
): string {
  return `<div class="whobar" id="whobar">
    <div class="whob-group">
      <span class="whob-lab">On it</span>
      <div class="whob-set">
        ${techs.length ? techs.map((t) => bubble(t, "technician")).join("")
          : bubble(null, "technician")}
        ${mayEdit ? `<button class="whob-edit" id="whob-edit">Change</button>` : ""}
      </div>
    </div>
    <div class="whob-group">
      <span class="whob-lab">Advisor</span>
      <div class="whob-set">
        ${mayEdit && desk.length
          ? `<span class="whob-pick" style="--crew:${advisor ? crewHue(advisor.id) : 0}">
              <i class="whob-dot">${advisor ? esc(initials(advisor.name)) : "?"}</i>
              <select id="whob-advisor" aria-label="Service advisor">
                <option value=""${advisor ? "" : " selected"}>Nobody yet</option>
                ${desk.map((d) => `<option value="${esc(d.id)}"${
                  advisor?.id === d.id ? " selected" : ""}>${esc(d.name)}</option>`).join("")}
              </select></span>`
          : bubble(advisor, "advisor")}
      </div>
    </div>
  </div>`;
}

/** Everybody who could be an advisor. Staff, not technicians. */
export async function advisors(): Promise<Person[]> {
  const sb = await db();
  const { data } = await sb.from("profiles")
    .select("id,full_name,role,active")
    .eq("active", true)
    .in("role", ["admin", "supervisor", "advisor", "staff"])
    .order("full_name");
  return ((data ?? []) as Array<{ id: string; full_name: string }>)
    .map((p) => ({ id: p.id, name: p.full_name }));
}

export async function setAdvisor(jobId: string, advisorId: string | null): Promise<void> {
  const sb = await db();
  const { error } = await sb.from("jobs").update({ advisor_id: advisorId }).eq("id", jobId);
  if (error) throw new Error(error.message);
}
